/**
 * Gomitolo featured-products tabs.
 *
 * Progressive-enhancement web component for the "Prodotti in Vetrina" section.
 * - Builds the tab bar from the tab blocks' buttons; it follows the ARIA tabs pattern (roving tabindex, arrow / Home / End keys).
 *
 * Collapse / inactive-panel hiding is driven by CSS scoped to `:defined`, so with
 * JavaScript disabled every panel and every card stays visible (nothing is trapped
 * behind a control that cannot fire). No flash of the full list occurs once the
 * element upgrades because the CSS applies before `connectedCallback` runs.
 */
class GomitoloFeaturedTabs extends HTMLElement {
  connectedCallback() {
    // Each tab is a theme block that renders its own button and panel; gather the buttons into one tab bar.
    const items = Array.from(this.querySelectorAll('[data-ft-item]'));
    this.tabs = items
      .map((item) => item.querySelector('[role="tab"]'))
      .filter(Boolean);
    if (this.tabs.length === 0) return;

    let list = this.querySelector('[role="tablist"]');
    if (!list) {
      list = this.ownerDocument.createElement('div');
      list.className = 'gomitolo-ft__tablist';
      list.setAttribute('role', 'tablist');
      const label = this.getAttribute('data-tablist-label');
      if (label) list.setAttribute('aria-label', label);
      this.prepend(list);
    }
    this.tabs.forEach((tab) => list.appendChild(tab));

    this.panels = this.tabs.map((tab) =>
      this.ownerDocument.getElementById(tab.getAttribute('aria-controls'))
    );

    this.tabs.forEach((tab, index) => {
      tab.addEventListener('click', () => this.selectTab(index));
      tab.addEventListener('keydown', (event) => this.onKeydown(event, index));
    });

    // The section header's "Vedi tutti" link follows the active tab's collection; the per-tab link below the row is then
    // redundant and hidden. Without JavaScript the header link keeps its all-products target and the per-tab links stay.
    this.viewAll = this.closest('.gomitolo-ft')?.querySelector('.gomitolo-ft__catalog-link') || null;
    if (this.viewAll) this.setAttribute('data-view-all-synced', '');

    // First tab selected on load.
    let initial = this.tabs.findIndex(
      (tab) => tab.getAttribute('aria-selected') === 'true'
    );
    if (initial < 0) initial = 0;
    this.selectTab(initial, false);
  }

  selectTab(index, moveFocus = true) {
    this.tabs.forEach((tab, i) => {
      const selected = i === index;
      tab.setAttribute('aria-selected', selected ? 'true' : 'false');
      tab.setAttribute('tabindex', selected ? '0' : '-1');

      const panel = this.panels[i];
      if (panel) {
        panel.hidden = !selected;
        if (selected) {
          panel.removeAttribute('data-ft-inactive');
        } else {
          panel.setAttribute('data-ft-inactive', '');
        }
      }
    });

    const url = this.panels[index]?.getAttribute('data-collection-url');
    if (this.viewAll && url) this.viewAll.setAttribute('href', url);

    if (moveFocus) this.tabs[index].focus();
  }

  onKeydown(event, index) {
    const last = this.tabs.length - 1;
    let next;

    switch (event.key) {
      case 'ArrowRight':
      case 'ArrowDown':
        next = index === last ? 0 : index + 1;
        break;
      case 'ArrowLeft':
      case 'ArrowUp':
        next = index === 0 ? last : index - 1;
        break;
      case 'Home':
        next = 0;
        break;
      case 'End':
        next = last;
        break;
      default:
        return;
    }

    event.preventDefault();
    this.selectTab(next);
  }

}

if (!customElements.get('gomitolo-featured-tabs')) {
  customElements.define('gomitolo-featured-tabs', GomitoloFeaturedTabs);
}
